import React, { useCallback, useMemo } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { Hit } from 'instantsearch.js';
import { Highlight } from 'react-instantsearch-hooks-web';
import { Category } from '@shared/types/product/Category';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import Link from 'next/link';
import { localeMapping } from '@/constants/localeMapping';
import { getLifecycleBadge } from '@/utils/product-lifecycle';


interface Props {
  hit: any;
  categories: any;
  onClick?: (x: string) => void;
  isFromDeliveryEstimator?: boolean;
  exclusiveProduct?: Record<string, string[]>;
}
import { exclusivityMapping } from '@/constants/exclusivityMapping';

const SearchItem: React.FC<Props> = ({
  hit,
  categories,
  onClick,
  isFromDeliveryEstimator = false,
}) => {
  const router = useRouter();
  const { locale } = useParams();
  const localeString = Array.isArray(locale) ? locale[0] : locale || '';
  const { selectedBusinessUnit } = useStoreAndBusinessUnits();

  const primaryCategory = useMemo(
    () => categories?.find((category: { categoryId: any }) => category.categoryId === hit.categories?.[0]?.categoryId),
    [categories, hit],
  );

  const variant = useMemo(() => hit.variants?.[0], [hit]);
  // const discountedVariant = useVariantWithDiscount(hit.variants as Partial<Variant>[]);

  const handleClick = useCallback(() => {
    onClick?.(hit.attributes['item-code'] as string);

    // gtag('event', AUTOCOMPLETE_PRODUCT_CLICKED, hit);

    !isFromDeliveryEstimator && router.push(hit._url ?? '#');
  }, [hit, onClick, router]);

  const localeKey = localeMapping[localeString];

  const exclusiveProducts: any = {
    au: hit?.attributes?.['exclusive-product-AU'],
    nz: hit?.attributes?.['exclusive-product-NZ'],
  };
  const exclusiveProductValue = useMemo(() => {
  let val;

  if (locale === 'au') {
    val = hit?.attributes?.['exclusive-product-AU'];
  } else if (localeString === 'en-NZ') {
    val = hit?.attributes?.['exclusive-product-NZ'];
  }

  if (!Array.isArray(val)) return [];

  return val.filter(
    (v) => v && String(v).trim().toLowerCase() !== 'null'
  );
}, [hit, localeString]);
  const isFormaRange = useMemo(() => {
    const rangeVal = hit?.attributes?.['range'];

    const checkRange = (val: string | any) => {
      if (typeof val === 'string') {
        const v = val.toLowerCase();
        return v === 'forma' || v === 'forma ii';
      }
      if (val && typeof val === 'object' && val['en-AU']) {
        const v = val['en-AU'].toLowerCase();
        return v === 'forma' || v === 'forma ii';
      }
      return false;
    };

    return checkRange(rangeVal);
  }, [hit]);

  const isPLUMWO = useMemo(() => {
    const extractAttr = (val: any): any => {
      if (!val) return val;
      if (Array.isArray(val)) return val[0];
      if (typeof val === 'object' && val !== null) return val.key || val.label || val;
      return val;
    };
    const exclusiveProductNZ = extractAttr(hit?.attributes?.['exclusive-product-NZ']);
    return exclusiveProductNZ === 'PLUMWO';
  }, [hit]);

  const exclusiveProduct = Array.isArray(exclusiveProducts[localeString]) && exclusiveProducts[localeString].length > 0 && exclusiveProducts[localeString][0]?.toLowerCase() !== 'null';
  const isNew = hit?.attributes?.IsNew;
  const lifecycle = hit?.attributes?.['item-attributes']?.find((attr: { name: string }) => attr.name === 'lifecycle')
    ?.value[localeKey];
  const lifecycleBadge = useMemo(() => getLifecycleBadge(lifecycle), [lifecycle]);

  return (
    <Link href={!isFromDeliveryEstimator ? hit._url : '#'} onMouseUp={handleClick}>
      <div id="searchBody" className="product-suggestions flex items-center gap-2 pl-4">
        <div className="shrink-0">
          <div className="h-70 w-70 relative">
            {/* EXCLUSIVE badge */}
            {exclusiveProductValue
              ?.filter((p) => p && String(p).trim().toLowerCase() !== 'null')
              .map((p, index) => {
                const partnerName = exclusivityMapping[p?.toUpperCase()] || String(p).toUpperCase();
                return (
                  <div key={index} className="absolute bottom-0 left-0 z-20  border border-[#ffffff] !bg-[#A6C9CC] !text-[#000000] px-0.5 py-0.2 text-[3px] font-semibold shadow-sm font-sans whitespace-nowrap transform scale-[0.65] origin-bottom-left uppercase">
                    EXCLUSIVE TO {partnerName}
                  </div>
                );
              })}

            {/* {isNew && !lifecycleBadge && (
              <div className={`ml-0.5 ${isFormaRange || isPLUMWO ? 'top-3.5' : 'top-0.5'} px-1 py-0.5 rounded-[4px] text-white bg-[#FA2727] absolute z-10 flex justify-center items-center text-[5px] font-semibold uppercase`}>
                NEW
              </div>
            )} */}
            {lifecycleBadge && (
              <div
                className={`absolute left-[1px] ${exclusiveProductValue.length > 0 ? (isFormaRange || isPLUMWO ? 'top-1' : 'top-4') : (isFormaRange || isPLUMWO ? 'top-3.5' : 'top-1')} px-1 py-0.5 rounded-[4px] text-white z-10 flex justify-center items-center text-[2px] font-semibold uppercase`}
                style={{ backgroundColor: lifecycleBadge.color }}
              >
                {lifecycleBadge.label}
              </div>
            )}

            <img
              src={hit?.images?.[0] ? hit?.images?.[0] : '/images/No_product_Image.jpg'}
              className="h-12 w-12"
              alt="Product"
            />
          </div>

        </div>
        <div>
          <p className="text-primary-black pl-2 text-16 uppercase">
            <Highlight attribute="name" hit={hit} />
          </p>
          <div className="flex flex-col">
            <span id="searchBody" className="text-16 font-medium text-[#333]">{hit?.name}</span>
            <span id="searchBody" className="text-14 font-normal  text-[#666]">{hit?.attributes['item-code']}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default SearchItem;